import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, FileCheck, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface Artifact {
  id: string;
  company_id: string;
  artifact_type: string;
  source_module: string;
  file_path: string;
  file_hash_sha256: string | null;
  source_hash_sha256: string | null;
  onchain_receipt_id: string | null;
  status: string;
  generated_at: string;
}

export function EvidenceCenter() {
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [loading, setLoading] = useState(true);

  async function loadArtifacts() {
    setLoading(true);
    const { data, error } = await supabase
      .from('generated_artifacts')
      .select('*')
      .order('generated_at', { ascending: false })
      .limit(100);

    if (error) {
      toast.error('Failed to load artifacts');
      console.error(error);
    } else {
      setArtifacts(data || []);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadArtifacts();
  }, []);

  const orphaned = artifacts.filter((a) => !a.file_hash_sha256 && a.status === 'active');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <FileCheck className="h-5 w-5 text-primary" />
          Evidence Center
        </h2>
        <Button variant="outline" size="sm" onClick={loadArtifacts}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {orphaned.length > 0 && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-medium">
                {orphaned.length} artifact(s) missing hash verification
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Generated Artifacts ({artifacts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Hash</TableHead>
                    <TableHead>On-chain</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Generated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {artifacts.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>
                        <Badge variant="outline">{a.artifact_type}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {a.source_module}
                      </TableCell>
                      <TableCell className="text-sm font-mono max-w-[200px] truncate">
                        {a.file_path}
                      </TableCell>
                      <TableCell>
                        {a.file_hash_sha256 ? (
                          <span className="text-xs font-mono text-muted-foreground">
                            {a.file_hash_sha256.slice(0, 12)}…
                          </span>
                        ) : (
                          <Badge variant="destructive" className="text-xs">
                            Missing
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {a.onchain_receipt_id ? (
                          <Badge className="bg-emerald-500/10 text-emerald-600 text-xs">
                            Linked
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={a.status === 'active' ? 'default' : 'secondary'}>
                          {a.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(a.generated_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                  {artifacts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No artifacts recorded yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
